import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { createEmptyProject } from '@/features/editor/store/editorStore';
import { useEditorStore } from '@/features/editor/store/editorStore';
import { saveProject } from '@/features/editor/engine/projectStorage';
import { clearActiveAsset } from '@/features/editor/renderer/rendererBridge';

interface AspectPreset {
  ratio: number;
  label: string;
  platforms: string;
  /** Visual thumbnail dimensions (relative) */
  thumbW: number;
  thumbH: number;
}

const PRESETS: AspectPreset[] = [
  { ratio: 16 / 9, label: '16:9', platforms: 'YouTube, Facebook', thumbW: 32, thumbH: 18 },
  { ratio: 9 / 16, label: '9:16', platforms: 'TikTok, YouTube, Instagram, Facebook', thumbW: 18, thumbH: 32 },
  { ratio: 1, label: '1:1', platforms: 'Instagram, LinkedIn, Facebook', thumbW: 24, thumbH: 24 },
];

export function CreateNewPopover({ collapsed }: { collapsed: boolean }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  function handleCreate(aspect: number) {
    clearActiveAsset();
    const project = createEmptyProject();
    project.projectAspect = aspect;
    useEditorStore.getState().loadProject(project);
    saveProject(project).catch(console.error);
    navigate(`/studio/${project.id}`);
    setOpen(false);
  }

  return (
    <div ref={containerRef} className="relative">
      {/* Create New button */}
      <button
        onClick={() => setOpen(!open)}
        title={collapsed ? t('nav.createNew', 'Create new') : undefined}
        className={`flex w-full items-center gap-2.5 rounded-xl font-bold transition-all duration-200 hover:brightness-110 active:scale-[0.97] ${
          collapsed ? 'h-10 w-10 justify-center' : 'px-3 py-2.5'
        }`}
        style={{
          background: 'linear-gradient(135deg, var(--user-page-accent) 0%, #5ce0d6 100%)',
          color: '#000',
        }}
      >
        <Plus size={18} strokeWidth={2.5} className="flex-shrink-0" />
        {!collapsed && (
          <span className="text-sm tracking-tight">{t('nav.createNew', 'Create new')}</span>
        )}
      </button>

      {/* Popover dropdown */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.96 }}
            transition={{ duration: 0.15 }}
            className="absolute z-50 mt-2 w-56 rounded-2xl border shadow-xl overflow-hidden"
            style={{
              left: collapsed ? '52px' : '0',
              top: collapsed ? '-8px' : undefined,
              backgroundColor: 'var(--user-page-card-bg, #fff)',
              borderColor: 'var(--user-page-card-border, #e5e7eb)',
              boxShadow: '0 12px 40px rgba(0,0,0,0.2), 0 4px 12px rgba(0,0,0,0.1)',
            }}
          >
            <div className="px-4 pt-4 pb-2">
              <p className="text-sm font-bold" style={{ color: 'var(--user-page-heading, #000)' }}>
                {t('nav.createNewVideo', 'Video')}
              </p>
            </div>

            {/* Blank canvas header */}
            <button
              onClick={() => handleCreate(16 / 9)}
              className="flex w-full items-center gap-3 px-4 py-2.5 transition-colors hover:bg-[var(--user-nav-hover-bg,#f3f4f6)]"
            >
              <div
                className="flex h-8 w-8 items-center justify-center rounded-lg flex-shrink-0"
                style={{ border: '1.5px dashed var(--user-page-text, #666)', opacity: 0.4 }}
              >
                <Plus size={14} strokeWidth={2} style={{ color: 'var(--user-page-text, #666)' }} />
              </div>
              <span className="text-sm font-semibold" style={{ color: 'var(--user-page-heading, #000)' }}>
                {t('nav.blankCanvas', 'Blank canvas')}
              </span>
            </button>

            {/* Divider */}
            <div className="mx-4 h-px" style={{ backgroundColor: 'var(--user-page-card-border, #e5e7eb)' }} />

            {/* Aspect ratio presets */}
            {PRESETS.map((preset) => (
              <button
                key={preset.label}
                onClick={() => handleCreate(preset.ratio)}
                className="flex w-full items-center gap-3 px-4 py-3 transition-colors hover:bg-[var(--user-nav-hover-bg,#f3f4f6)]"
              >
                {/* Aspect ratio thumbnail */}
                <div className="flex h-8 w-8 items-center justify-center flex-shrink-0">
                  <div
                    className="rounded-[3px]"
                    style={{
                      width: `${preset.thumbW}px`,
                      height: `${preset.thumbH}px`,
                      backgroundColor: 'var(--user-page-heading, #000)',
                    }}
                  />
                </div>
                <div className="min-w-0 text-start">
                  <p className="text-sm font-semibold" style={{ color: 'var(--user-page-heading, #000)' }}>
                    {preset.label}
                  </p>
                  <p className="text-[11px] truncate" style={{ color: 'var(--user-page-text, #666)', opacity: 0.5 }}>
                    {preset.platforms}
                  </p>
                </div>
              </button>
            ))}

            {/* Bottom padding */}
            <div className="h-2" />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
